'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    InputMask,
    InputMaskProps,
    InputMaskChangeEvent
} from 'primereact/inputmask';
import {
    InputNumber,
    InputNumberProps,
    InputNumberChangeEvent
} from 'primereact/inputnumber';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    DurationFieldPrecision,
    DurationFieldPresentation,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';
import {
    convertToDouble,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import StaticContentField, {
    componentProps as staticContentFieldComponentProps,
    ComponentProps as StaticContentFieldComponentProps
} from '~/frontend/src/components/base/shared/field/StaticContentField';

import {doubleRenderer} from '~/shared/src/utilities/valueRenderers';
import {StoreState} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';


type InputProps = Omit<InputNumberProps, 'onChange' | 'key'>;
const inputProps = keys<InputProps>();
type MaskProps = Omit<InputMaskProps, 'onChange' | 'key'>;
const maskProps = keys<MaskProps>();


type InputOptions = {
    mask : string;
    format : string;
    hasDays : boolean;
    hasHours : boolean;
    hasMinutes : boolean;
    hasSeconds : boolean;
    hasMilliseconds : boolean;
    hasMicroseconds : boolean;
};

type DefaultProps = FieldWrapperDefaultProps & {

    limitToStepWidth? : boolean;
    precision? : DurationFieldPrecision;
    presentation? : DurationFieldPresentation;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    inputClassNames : [],
    inputStyle      : {},
    limitToStepWidth: false,
    markDirty       : false,
    precision       : 'minute',
    presentation    : 'picker',
    rawMode         : false,
    useHookForm     : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : number;

    /**
     * The value to determine dirty against.
     */
    originalValue? : number;

    stepWidth? : number;

    inputFormat? : string; // e.g. 'dd:hh:mm:ss.ssssss', 'hh:mm'

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : number | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : number | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props & {
    onBlur : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
    onFocus : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
};


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const DurationField = React.forwardRef<InputNumber | InputMask, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  autoComplete,
                  disabled,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputFormat,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  limitToStepWidth,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  precision,
                  presentation,
                  pt,
                  rawMode,
                  stepWidth,
                  tabIndex,
                  uid,
                  useHookForm,
                  value
              }                                                 = props,
              elementId                                         = id ?? `${uid}_durationField`,
              inValue                                           = useRef<number | undefined>( value ),
              outValue                                          = useRef<number | undefined>( value ),
              elementValuePartsCurrent                          = useRef<{
                  days : number;
                  hours : number;
                  minutes : number;
                  seconds : number;
                  milliseconds : number;
                  microseconds : number;
              }>( { days: 0, hours: 0, minutes: 0, seconds: 0, milliseconds: 0, microseconds: 0 } ),
              timeoutId                                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ]                 = useState<number | undefined>( value ),
              [ lastMaskValue, setLastMaskValue ]               = useState<string | undefined>( undefined ),
              [ focus, setFocus ]                               = useState<boolean>( false ),
              [ errorMessage, setErrorMessage ]                 = useState<string>( '' ),
              [ internalErrorMessage, setInternalErrorMessage ] = useState<string>( '' ),
              messageResolver                                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              fieldWrapperAutoCompleteConfig                    = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue                  : elementValue !== undefined ? convertValueToString( elementValue, 'DURATION' ) : '',
                              autoCompleteInputHasFocus          : focus,
                              onAutoCompleteValueChange          : ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  const autoCompleteValue = convertToDouble( autoCompleteValueRaw ) as number;

                                  if ( !isNaN( autoCompleteValue ) && autoCompleteValue !== elementValue ) {
                                      setElementValue( autoCompleteValue );

                                      onChange( autoCompleteValue, originalEvent );
                                  }
                              },
                              autoCompleteSuggestionsItemTemplate: ( suggestion : string ) => {
                                  return doubleRenderer( suggestion, undefined, undefined, rawMode );
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange
                  ]
              ),
              days                                              = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( elementValue / 86400 );
                      }

                      return retVal;
                  },
                  [
                      elementValue
                  ]
              ),
              hours                                             = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( ( elementValue - days * 86400 ) / 3600 );
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      days
                  ]
              ),
              minutes                                           = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( ( elementValue - ( days * 86400 + hours * 3600 ) ) / 60 );
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      days,
                      hours
                  ]
              ),
              seconds                                           = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( ( elementValue - ( days * 86400 + hours * 3600 + minutes * 60 ) ) );
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      days,
                      hours,
                      minutes
                  ]
              ),
              milliseconds                                      = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( ( elementValue - Math.floor( elementValue ) ) * 1000 );
                      }

                      return retVal;
                  },
                  [
                      elementValue
                  ]
              ),
              microseconds                                      = useMemo(
                  () => {
                      let retVal = 0;

                      if ( elementValue ) {
                          retVal = Math.floor( ( elementValue - Math.floor( elementValue ) ) * 1000000 );
                      }

                      return retVal;
                  },
                  [
                      elementValue
                  ]
              ),
              originalDays                                      = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( originalValue / 86400 );
                      }

                      return retVal;
                  },
                  [
                      originalValue
                  ]
              ),
              originalHours                                     = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( ( originalValue - originalDays * 86400 ) / 3600 );
                      }

                      return retVal;
                  },
                  [
                      originalValue,
                      originalDays
                  ]
              ),
              originalMinutes                                   = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( ( originalValue - ( originalDays * 86400 + originalHours * 3600 ) ) / 60 );
                      }

                      return retVal;
                  },
                  [
                      originalValue,
                      originalDays,
                      originalHours
                  ]
              ),
              originalSeconds                                   = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( ( originalValue - ( originalDays * 86400 + originalHours * 3600 + originalMinutes * 60 ) ) );
                      }

                      return retVal;
                  },
                  [
                      originalValue,
                      originalDays,
                      originalHours,
                      originalMinutes
                  ]
              ),
              originalMilliseconds                              = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( ( originalValue - Math.floor( originalValue ) ) * 1000 );
                      }

                      return retVal;
                  },
                  [
                      originalValue
                  ]
              ),
              originalMicroseconds                              = useMemo(
                  () => {
                      let retVal = 0;

                      if ( originalValue ) {
                          retVal = Math.floor( ( originalValue - Math.floor( originalValue ) ) * 1000000 );
                      }

                      return retVal;
                  },
                  [
                      originalValue
                  ]
              ),
              inputOptions                                      = useMemo(
                  () => {
                      const retVal : InputOptions = {
                          mask           : '',
                          format         : '',
                          hasDays        : false,
                          hasHours       : false,
                          hasMinutes     : false,
                          hasSeconds     : false,
                          hasMilliseconds: false,
                          hasMicroseconds: false
                      };

                      // validate given input format
                      if ( inputFormat ) {
                          if ( inputFormat?.match( /(^dd|((^|dd:)(hh|(^|hh:)(mm|(^|mm:)(ss|(^|ss\.)(sss|ssssss))))))$/gi ) ) {
                              retVal.format = inputFormat.toLowerCase();
                          }
                          else {
                              setInternalErrorMessage( `given inputFormat '${inputFormat}' is invalid` );
                          }
                      }

                      // build format if not given or not valid
                      if ( !retVal.format ) {
                          switch ( precision ) {
                              case 'microsecond':
                                  retVal.format = `sss${retVal.format}`;
                              case 'millisecond':
                                  retVal.format = `sss${retVal.format}`;
                              case 'second':
                                  retVal.format = `ss${retVal.format ? '.' : ''}${retVal.format}`;
                              case 'minute':
                                  retVal.format = `mm${retVal.format ? ':' : ''}${retVal.format}`;
                              case 'hour':
                                  retVal.format = `hh${retVal.format ? ':' : ''}${retVal.format}`;
                              case 'day':
                                  retVal.format = `dd${retVal.format ? ':' : ''}${retVal.format}`;
                          }
                      }

                      // build mask and has flags from format
                      if ( retVal?.format ) {
                          retVal.mask = retVal.format.replaceAll( /[a-z]/g, '9' );
                          retVal.hasDays = retVal.format.match( /(^)dd($|:)/ ) !== null;
                          retVal.hasHours = retVal.format.match( /(^|:)hh($|:)/ ) !== null;
                          retVal.hasMinutes = retVal.format.match( /(^|:)mm($|:)/ ) !== null;
                          retVal.hasSeconds = retVal.format.match( /(^|:)ss($|.)/ ) !== null;
                          retVal.hasMilliseconds = retVal.format.match( /(^|.)sss($)/ ) !== null;
                          retVal.hasMicroseconds = retVal.format.match( /(^|.)ssssss($)/ ) !== null;
                      }

                      return retVal;
                  },
                  [
                      precision,
                      inputFormat
                  ]
              ),
              pickerClassNames                                  = useMemo(
                  () => {
                      const retVal = {
                          days           : [ 'yn-subfield', 'yn-durationfield-days' ],
                          hours          : [ 'yn-subfield', 'yn-durationfield-hours' ],
                          minutes        : [ 'yn-subfield', 'yn-durationfield-minutes' ],
                          seconds        : [ 'yn-subfield', 'yn-durationfield-seconds' ],
                          fractionSeconds: [ 'yn-subfield', 'yn-durationfield-fractionSeconds' ]
                      };

                      if ( markDirty && !rawMode && presentation !== 'input' && elementValue !== originalValue ) {
                          if ( days !== originalDays ) {
                              retVal.days.push( 'yn-dirty-border' );
                          }
                          if ( hours !== originalHours ) {
                              retVal.hours.push( 'yn-dirty-border' );
                          }
                          if ( minutes !== originalMinutes ) {
                              retVal.minutes.push( 'yn-dirty-border' );
                          }
                          if ( seconds !== originalSeconds ) {
                              retVal.seconds.push( 'yn-dirty-border' );
                          }
                          if ( milliseconds !== originalMilliseconds || microseconds !== originalMicroseconds ) {
                              retVal.fractionSeconds.push( 'yn-dirty-border' );
                          }
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      markDirty,
                      originalValue,
                      presentation,
                      rawMode
                  ]
              ),
              buildChangedValueForPicker                        = useCallback(
                  ( type : 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds' | 'microseconds', valueToSet : number | null ) => {
                      let retVal : number | undefined = 0;

                      if ( valueToSet !== null ) {

                          if ( inputOptions.hasDays ) {
                              if ( type === 'days' ) {
                                  retVal += 86400 * valueToSet;
                              }
                              else {
                                  retVal += 86400 * elementValuePartsCurrent.current.days;
                              }
                          }
                          if ( inputOptions.hasHours ) {
                              if ( type === 'hours' ) {
                                  retVal += 3600 * valueToSet;
                              }
                              else {
                                  retVal += 3600 * elementValuePartsCurrent.current.hours;
                              }
                          }
                          if ( inputOptions.hasMinutes ) {
                              if ( type === 'minutes' ) {
                                  retVal += 60 * valueToSet;
                              }
                              else {
                                  retVal += 60 * elementValuePartsCurrent.current.minutes;
                              }
                          }
                          if ( inputOptions.hasSeconds ) {
                              if ( type === 'seconds' ) {
                                  retVal += valueToSet;
                              }
                              else {
                                  retVal += elementValuePartsCurrent.current.seconds;
                              }
                          }
                          if ( inputOptions.hasMilliseconds ) {
                              if ( type === 'milliseconds' ) {
                                  retVal += valueToSet / 1000;
                              }
                              else {
                                  retVal += elementValuePartsCurrent.current.milliseconds / 1000;
                              }
                          }
                          if ( inputOptions.hasMicroseconds ) {
                              if ( type === 'microseconds' ) {
                                  retVal += valueToSet / 1000000;
                              }
                              else {
                                  retVal += elementValuePartsCurrent.current.microseconds / 1000000;
                              }
                          }
                      }

                      if ( retVal === 0 ) {
                          retVal = undefined;
                      }

                      return retVal;
                  },
                  [
                      inputOptions
                  ]
              ),
              validateValueForStep                              = useCallback(
                  ( valueToValidate : number | undefined ) => {
                      let retVal : boolean = false;

                      if ( valueToValidate === undefined || !stepWidth || !limitToStepWidth ) {
                          retVal = true;
                      }
                      else if ( stepWidth && limitToStepWidth && valueToValidate % stepWidth === 0 ) {
                          retVal = true;
                      }

                      if ( retVal ) {
                          setErrorMessage( '' );
                      }
                      else {
                          setErrorMessage( __( 'YN.base.form.durationField.error.inputNotMultipleOfStepWidth', { stepWidth: String( stepWidth ) } ) );
                      }

                      return retVal;
                  },
                  [
                      limitToStepWidth,
                      stepWidth
                  ]
              ),
              validateValueForMask                              = useCallback(
                  ( valueToSet : string | null | undefined ) => {
                      let retVal : boolean = !valueToSet;

                      if ( valueToSet ) {
                          if ( !valueToSet.replaceAll( /:|\./g, '' ) ) {
                              retVal = true;
                          }
                          else {
                              let regExpString = '^';

                              if ( inputOptions.hasDays ) {
                                  regExpString += '([0-9]{2,2})';
                              }
                              if ( inputOptions.hasHours ) {
                                  regExpString += `${inputOptions.hasDays ? ':' : ''}(((0|1)[0-9])|2[0-3])`;
                              }
                              if ( inputOptions.hasMinutes ) {
                                  regExpString += `${inputOptions.hasHours ? ':' : ''}([0-5][0-9])`;
                              }
                              if ( inputOptions.hasSeconds ) {
                                  regExpString += `${inputOptions.hasMinutes ? ':' : ''}([0-5][0-9])`;
                              }
                              if ( inputOptions.hasMilliseconds ) {
                                  regExpString += `${inputOptions.hasSeconds ? '\.' : ''}([0-9]{3,3})`;
                              }
                              if ( inputOptions.hasMicroseconds ) {
                                  regExpString += `${inputOptions.hasSeconds ? '\.' : ''}([0-9]{6,6})`;
                              }

                              const regExp = new RegExp( regExpString );

                              retVal = !!valueToSet.match( regExp );
                          }
                      }

                      if ( retVal ) {
                          setErrorMessage( '' );
                      }
                      else {
                          setErrorMessage( __( 'YN.base.form.durationField.error.inputNotValid', { format: inputOptions.format } ) );
                      }

                      return retVal;
                  },
                  [
                      inputOptions
                  ]
              ),
              buildChangedValueForMask                          = useCallback(
                  ( valueToSet : string | null | undefined ) => {
                      let retVal : number | undefined = 0;

                      if ( valueToSet ) {
                          if ( !valueToSet.replaceAll( /:|\./g, '' ) ) {
                              retVal = undefined;
                          }
                          else {
                              const valueToSetParts = valueToSet.split( /:|\./ );

                              if ( inputOptions.hasDays ) {
                                  retVal += 86400 * Number( valueToSetParts.shift() );
                              }
                              if ( inputOptions.hasHours ) {
                                  retVal += 3600 * Number( valueToSetParts.shift() );
                              }
                              if ( inputOptions.hasMinutes ) {
                                  retVal += 60 * Number( valueToSetParts.shift() );
                              }
                              if ( inputOptions.hasSeconds ) {
                                  retVal += Number( valueToSetParts.shift() );
                              }
                              if ( inputOptions.hasMilliseconds ) {
                                  retVal += Number( valueToSetParts.shift() ) / 1000;
                              }
                              if ( inputOptions.hasMicroseconds ) {
                                  retVal += Number( valueToSetParts.shift() ) / 1000000;
                              }
                          }
                      }

                      if ( retVal === 0 ) {
                          retVal = undefined;
                      }

                      return retVal;
                  },
                  [
                      inputOptions
                  ]
              ),
              onPickerChange                                    = useCallback(
                  ( type : 'days' | 'hours' | 'minutes' | 'seconds', event : InputNumberChangeEvent ) => {
                      const changedValue = buildChangedValueForPicker( type, event.value );

                      if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange,
                      validateValueForStep
                  ]
              ),
              onHookFormPickerChange                            = useCallback(
                  ( type : 'days' | 'hours' | 'minutes' | 'seconds', event : InputNumberChangeEvent ) => {
                      const changedValue = buildChangedValueForPicker( type, event.value );

                      if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      validateValueForStep
                  ]
              ),
              onInputChange                                     = useCallback(
                  ( event : InputMaskChangeEvent ) => {
                      if ( validateValueForMask( event.value ) ) {
                          const changedValue = buildChangedValueForMask( event.value );

                          if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                              setElementValue( changedValue );

                              onChange( changedValue, event );
                          }
                      }
                  },
                  [
                      onChange,
                      validateValueForMask,
                      validateValueForStep
                  ]
              ),
              onHookFormInputChange                             = useCallback(
                  ( event : InputMaskChangeEvent ) => {
                      if ( validateValueForMask( event.value ) ) {
                          const changedValue = buildChangedValueForMask( event.value );

                          if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                              setElementValue( changedValue );

                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      validateValueForMask,
                      validateValueForStep
                  ]
              ),
              onRawInputChange                                  = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = event.value === 0 || event.value === null ? undefined : event.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormRawInputChange                          = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = event.value === 0 || event.value === null ? undefined : event.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onStep                                            = useCallback(
                  ( direction : 'inc' | 'dec', event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      let changedValue : number | undefined = outValue.current ?? 0;

                      if ( stepWidth ) {
                          // increment to next step up
                          if ( direction === 'inc' ) {
                              changedValue = ( Math.floor( changedValue / stepWidth ) + 1 ) * stepWidth;
                          }
                          else {
                              changedValue = ( Math.ceil( changedValue / stepWidth ) - 1 ) * stepWidth;
                          }
                      }

                      if ( changedValue <= 0 ) {
                          changedValue = undefined;
                      }

                      if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange,
                      stepWidth,
                      validateValueForStep
                  ]
              ),
              onHookFormStep                                    = useCallback(
                  ( direction : 'inc' | 'dec', event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      let changedValue : number | undefined = outValue.current ?? 0;

                      if ( stepWidth ) {
                          // increment to next step up
                          if ( direction === 'inc' ) {
                              changedValue = ( Math.floor( changedValue / stepWidth ) + 1 ) * stepWidth;
                          }
                          else {
                              changedValue = ( Math.ceil( changedValue / stepWidth ) - 1 ) * stepWidth;
                          }
                      }

                      if ( changedValue <= 0 ) {
                          changedValue = undefined;
                      }

                      if ( validateValueForStep( changedValue ) && changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange,
                      stepWidth,
                      validateValueForStep
                  ]
              ),
              onInputFocus                                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              fieldInput                                        = useMemo(
                  () => {
                      let retVal : React.ReactNode | null = null;

                      // internal error
                      if ( internalErrorMessage ) {
                          retVal = <StaticContentField
                              {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                              label={undefined}
                              labelVisibility="hidden"
                              infoTextLayout="text"
                              height={1}
                              content={internalErrorMessage}
                              inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                              messageClassNames={[ 'hidden' ]}
                              inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                              style={{ marginBottom: 0 }}
                          />;
                      }

                      // raw input
                      else if ( rawMode ) {
                          retVal = <InputNumber
                              {..._.pick( props, inputProps ) as InputProps}

                              id={elementId}

                              ref={( node ) => {
                                  if ( typeof forwardedRef === 'function' ) {
                                      forwardedRef( node );
                                  }
                                  else if ( forwardedRef ) {
                                      forwardedRef.current = node;
                                  }
                              }}

                              value={elementValue}

                              onChange={useHookForm ? onHookFormRawInputChange : onRawInputChange}
                              onFocus={onInputFocus}
                              onBlur={onInputBlur}

                              locale="en-US"
                              useGrouping={false}
                              mode="decimal"
                              minFractionDigits={0}
                              maxFractionDigits={6}

                              className={`yn-durationfield ${inputClassNames.join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}

                              placeholder={resolvedPlaceholder}
                              tooltip={undefined}
                              tooltipOptions={undefined}

                              aria-describedby={`${elementId}_message`}

                              aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                              aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                              aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
                          />;
                      }

                      // picker
                      else if ( presentation === 'picker' ) {

                          // TODO: add field for milli/micro seconds if needed

                          retVal = <div id={elementId} className="yn-durationfield">
                              {inputOptions.hasDays &&
                                  <div className="field flex-grow-0 justifycenter text-center mb-0 mr-0" style={{ width: '4rem' }}>
                                      <label
                                          htmlFor={`${elementId}_days`}
                                          className="block mt-1 yn-sublabel"
                                      >
                                          {__( 'YN.base.common.daysShort' )}
                                      </label>
                                      <InputNumber
                                          {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                                          inputId={`${elementId}_days`}

                                          value={days}

                                          onChange={useHookForm ? onHookFormPickerChange.bind( null, 'days' ) : onPickerChange.bind( null, 'days' )}
                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          className={`${inputClassNames.join( ' ' )} ${pickerClassNames.days.join( ' ' )}`}
                                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                          tooltip={undefined}
                                          tooltipOptions={undefined}

                                          mode="decimal"
                                          showButtons={!stepWidth}
                                          buttonLayout="vertical"
                                          incrementButtonClassName="p-button-outlined"
                                          incrementButtonIcon="pi pi-plus"
                                          decrementButtonClassName="p-button-outlined"
                                          decrementButtonIcon="pi pi-minus"
                                      />
                                  </div>
                              }
                              {inputOptions.hasHours &&
                                  <div
                                      className={`field flex-grow-0 justifycenter text-center mb-0 mr-0 ${inputOptions.hasDays ? ' ml-2' : ''}`}
                                      style={{ width: '2.5rem' }}>
                                      <label
                                          htmlFor={`${elementId}_hours`}
                                          className="block mt-1 yn-sublabel"
                                      >
                                          {__( 'YN.base.common.hoursShort' )}
                                      </label>
                                      <InputNumber
                                          {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                                          inputId={`${elementId}_hours`}

                                          value={hours}

                                          onChange={useHookForm ? onHookFormPickerChange.bind( null, 'hours' ) : onPickerChange.bind( null, 'hours' )}
                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          className={`${inputClassNames.join( ' ' )} ${pickerClassNames.hours.join( ' ' )}`}
                                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                          tooltip={undefined}
                                          tooltipOptions={undefined}

                                          mode="decimal"
                                          min={0}
                                          max={23}
                                          showButtons={!stepWidth}
                                          buttonLayout="vertical"
                                          incrementButtonClassName="p-button-outlined"
                                          incrementButtonIcon="pi pi-plus"
                                          decrementButtonClassName="p-button-outlined"
                                          decrementButtonIcon="pi pi-minus"
                                      />
                                  </div>
                              }
                              {inputOptions.hasMinutes &&
                                  <div
                                      className={`field flex-grow-0 justifycenter text-center mb-0 mr-0 ${inputOptions.hasHours ? ' ml-2' : ''}`}
                                      style={{ width: '2.5rem' }}>
                                      <label
                                          htmlFor={`${elementId}_minutes`}
                                          className="block mt-1 yn-sublabel"
                                      >
                                          {__( 'YN.base.common.minutesShort' )}
                                      </label>
                                      <InputNumber
                                          {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                                          inputId={`${elementId}_minutes`}

                                          value={minutes}

                                          onChange={useHookForm ? onHookFormPickerChange.bind( null, 'minutes' ) : onPickerChange.bind( null, 'minutes' )}
                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          className={`${inputClassNames.join( ' ' )} ${pickerClassNames.minutes.join( ' ' )}`}
                                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                          tooltip={undefined}
                                          tooltipOptions={undefined}

                                          mode="decimal"
                                          min={0}
                                          max={59}
                                          showButtons={!stepWidth}
                                          buttonLayout="vertical"
                                          incrementButtonClassName="p-button-outlined"
                                          incrementButtonIcon="pi pi-plus"
                                          decrementButtonClassName="p-button-outlined"
                                          decrementButtonIcon="pi pi-minus"
                                      />
                                  </div>
                              }
                              {inputOptions.hasSeconds &&
                                  <div
                                      className={`field flex-grow-0 justifycenter text-center mb-0 mr-0 ${inputOptions.hasMinutes ? ' ml-2' : ''}`}
                                      style={{ width: '2.5rem' }}>
                                      <label
                                          htmlFor={`${elementId}_seconds`}
                                          className="block mt-1 yn-sublabel"
                                      >
                                          {__( 'YN.base.common.secondsShort' )}
                                      </label>
                                      <InputNumber
                                          {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                                          inputId={`${elementId}_seconds`}

                                          value={seconds}

                                          onChange={useHookForm ? onHookFormPickerChange.bind( null, 'seconds' ) : onPickerChange.bind( null, 'seconds' )}
                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          className={`${inputClassNames.join( ' ' )} ${pickerClassNames.seconds.join( ' ' )}`}
                                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                          tooltip={undefined}
                                          tooltipOptions={undefined}

                                          mode="decimal"
                                          min={0}
                                          max={59}
                                          showButtons={!stepWidth}
                                          buttonLayout="vertical"
                                          incrementButtonClassName="p-button-outlined"
                                          incrementButtonIcon="pi pi-plus"
                                          decrementButtonClassName="p-button-outlined"
                                          decrementButtonIcon="pi pi-minus"
                                      />
                                  </div>
                              }
                              {stepWidth &&
                                  <div
                                      className={`p-inputgroup yn-inputgroup yn-subfield yn-durationfield-stepper ${focus ? 'p-focus' : ''} ${disabled ? 'p-disabled' : ''}`}>
                                      <Button
                                          key={`${uid}_increaseByStep`}

                                          icon="pi pi-caret-up"
                                          className="yn-durationfield-stepper-up"
                                          onClick={useHookForm ? onHookFormStep.bind( null, 'inc' ) : onStep.bind( null, 'inc' )}

                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          disabled={disabled}

                                          tabIndex={tabIndex ? tabIndex + 10 : 0}
                                      />
                                      <Button
                                          key={`${uid}_decreaseByStep`}

                                          icon="pi pi-caret-down"
                                          className="yn-durationfield-stepper-down"
                                          onClick={useHookForm ? onHookFormStep.bind( null, 'dec' ) : onStep.bind( null, 'dec' )}

                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          disabled={disabled}

                                          tabIndex={tabIndex ? tabIndex + 20 : 0}
                                      />
                                  </div>
                              }
                          </div>;
                      }

                      // input
                      else {
                          let maskInputValue : string | undefined,
                              maskInputMask = inputOptions.mask;

                          if ( elementValue === undefined && lastMaskValue ) {
                              // to clear the InputMask component correctly set value to last one and toggle mask
                              maskInputValue = lastMaskValue;
                              maskInputMask = '';
                              setLastMaskValue( undefined );
                          }
                          else if ( elementValue ) {
                              maskInputValue = '';

                              if ( inputOptions.hasDays ) {
                                  maskInputValue += `${String( days ).padStart( 2, '0' )}`;
                              }
                              if ( inputOptions.hasHours ) {
                                  maskInputValue += `${inputOptions.hasDays ? ':' : ''}${String( hours ).padStart( 2, '0' )}`;
                              }
                              if ( inputOptions.hasMinutes ) {
                                  maskInputValue += `${inputOptions.hasHours ? ':' : ''}${String( minutes ).padStart( 2, '0' )}`;
                              }
                              if ( inputOptions.hasSeconds ) {
                                  maskInputValue += `${inputOptions.hasMinutes ? ':' : ''}${String( seconds ).padStart( 2, '0' )}`;
                              }
                              if ( inputOptions.hasMilliseconds ) {
                                  maskInputValue += `${inputOptions.hasSeconds ? '.' : ''}${milliseconds}`;
                              }
                              if ( inputOptions.hasMicroseconds ) {
                                  maskInputValue += `${inputOptions.hasSeconds ? '.' : ''}${microseconds}`;
                              }

                              setLastMaskValue( maskInputValue );
                          }

                          retVal = <InputMask
                              {..._.pick( props, maskProps ) as MaskProps}

                              key={`${elementId}_input`}
                              id={elementId}

                              ref={( node ) => {
                                  if ( typeof forwardedRef === 'function' ) {
                                      forwardedRef( node );
                                  }
                                  else if ( forwardedRef ) {
                                      forwardedRef.current = node;
                                  }
                              }}

                              value={maskInputValue}

                              onChange={useHookForm ? onHookFormInputChange : onInputChange}
                              onFocus={onInputFocus}
                              onBlur={onInputBlur}

                              mask={maskInputMask}
                              unmask={false}
                              slotChar={'_'}
                              autoClear={false}

                              className={`yn-durationfield ${inputClassNames.join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}

                              placeholder={resolvedPlaceholder ?? inputOptions.format}
                              tooltip={undefined}
                              tooltipOptions={undefined}

                              aria-describedby={`${elementId}_message`}

                              autoComplete={autoComplete && !disabled ? 'off' : 'on'}
                              aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                              aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                              aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
                          />;

                          if ( stepWidth ) {
                              retVal = <div key={`${elementId}_input_wrapper`} id={`${elementId}_wrapper`}
                                            className={`p-inputgroup yn-inputgroup yn-subfield ${focus ? 'p-focus' : ''} ${disabled ? 'p-disabled' : ''}`}>
                                  {retVal}
                                  <div className="yn-durationfield-stepper">
                                      <Button
                                          key={`${uid}_increaseByStep`}

                                          icon="pi pi-caret-up"
                                          className="yn-durationfield-stepper-up"
                                          onClick={useHookForm ? onHookFormStep.bind( null, 'inc' ) : onStep.bind( null, 'inc' )}

                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          disabled={disabled}

                                          tabIndex={tabIndex ? tabIndex + 10 : 0}
                                      />
                                      <Button
                                          key={`${uid}_decreaseByStep`}

                                          icon="pi pi-caret-down"
                                          className="yn-durationfield-stepper-down"
                                          onClick={useHookForm ? onHookFormStep.bind( null, 'dec' ) : onStep.bind( null, 'dec' )}

                                          onFocus={onInputFocus}
                                          onBlur={onInputBlur}

                                          disabled={disabled}

                                          tabIndex={tabIndex ? tabIndex + 20 : 0}
                                      />
                                  </div>
                              </div>;
                          }
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementId,
                      elementValue,
                      focus,
                      inputClassNames,
                      inputOptions,
                      inputStyle,
                      internalErrorMessage,
                      onHookFormRawInputChange,
                      onInputBlur,
                      onInputFocus,
                      onRawInputChange,
                      onPickerChange,
                      onHookFormPickerChange,
                      onInputChange,
                      onHookFormInputChange,
                      pickerClassNames,
                      presentation,
                      rawMode,
                      resolvedPlaceholder,
                      tabIndex,
                      useHookForm
                  ]
              );

        elementValuePartsCurrent.current = {
            days,
            hours,
            minutes,
            seconds,
            milliseconds,
            microseconds
        };

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            errorMessage={errorMessage ?? props.errorMessage}
            isDirty={( !rawMode || presentation === 'input' ) && markDirty && outValue.current !== originalValue}

            {...fieldWrapperAutoCompleteConfig}
            pt={pt?.fieldWrapper}
        >
            {fieldInput}
        </FieldWrapper>;
    }
} );
DurationField.defaultProps = defaultProps;
DurationField.displayName = 'DurationField';

export default DurationField;
